import { lockScroll, unlockScroll } from '@theme/dialog';

// While a nav overlay is open, hide Google's merchant badge (fixed, z-index 2^31-1, above any overlay).
// Shared with the header (mega panels) and the search overlay through window.XZV2Overlays.
function setOverlay(key, on) {
  const keys = window.XZV2Overlays || (window.XZV2Overlays = new Set());
  if (on) keys.add(key); else keys.delete(key);
  document.documentElement?.classList?.toggle('xz-v2-overlay-open', keys.size > 0);
}

// Tabbable descendants that are rendered (collapsed groups are display:none).
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const tabbables = (root) => [...root.querySelectorAll(TABBABLE)].filter((el) => el.getClientRects().length > 0);

// IX2 outQuad and inQuad, written exactly: the cubic form of a quadratic curve (x(t) = t, so y = the quadratic).
const OUT_QUAD = 'cubic-bezier(0.3333333, 0.6666667, 0.6666667, 1)';
const IN_QUAD = 'cubic-bezier(0.3333333, 0, 0.6666667, 0.3333333)';
const outQuad = (p) => p * (2 - p);
const inQuad = (p) => p * p;
// Progress of a Web Animation (0 while it waits for its first frame).
const progress = (animation, duration) => Math.min(1, Math.max(0, (animation?.currentTime || 0) / duration));

/* Menu artwork warm-up. Card images are lazy and sit in collapsed (display:none) groups of a closed dialog, so the
   browser only fetched one once its group expanded: tiles showed empty for 70-750ms, again on every page in Chrome.
   Nothing loads with the page (PageSpeed and visitors who never open the menu are unaffected). When the menu opens,
   after its first frame, every group's images are queued, the first screen of each group (FIRST_SCREEN tiles) before
   the rest, WARM_LIMIT at a time; expanding a group moves its own images to the front. (Started at the press on the
   menu button instead, the image requests made the menu's opening frame 6-15ms longer in WebKit; m18-nav perf.)
   Each is the card's own <img> switched to eager, so it fetches exactly the srcset candidate the card shows and the
   expanded group paints it in its first frame. The queue pauses while the menu is closed. The series artwork comes as
   progressive JPEG (image snippet): Shopify's CDN sends WebKit JPEG XL at 480w, which WebKit decodes far more slowly
   and partly on the main thread (m18-nav perf, 9 Oct 2026). No img.decode(): WebKit decodes JPEG XL synchronously
   there (~150ms per 480w picture), so pictures decoded ahead stalled the first expand for 0.9-1.3s (nav-images fix,
   5 Oct 2026), while Chrome paints them in the first frame without it. No fetchpriority=low: WebKit then held 10-70KB
   images back 300-900ms (iphone-images lane), and Chrome already loads images outside the viewport at low priority.
   Same queue as xz-v2-header.js (each module stays self-contained). */
const WARM_LIMIT = 6;
const FIRST_SCREEN = 6;
function warmQueue(limit) {
  const queue = [], seen = new WeakSet();
  let active = 0, running = false;
  const pump = () => {
    while (running && active < limit && queue.length) {
      const img = queue.shift();
      if (!img.isConnected || (img.complete && img.loading !== 'lazy')) continue;
      img.loading = 'eager';
      if (img.complete) continue;
      active++;
      let settled = false, timer = 0;
      const done = () => { if (settled) return; settled = true; clearTimeout(timer); active--; pump(); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      // Frees the slot if an image never settles.
      timer = setTimeout(done, 10000);
    }
  };
  return {
    // Queues images not seen before; `front` also moves queued ones ahead of the rest.
    add(imgs, front = false) {
      const next = [];
      for (const img of imgs) {
        if (!seen.has(img)) { seen.add(img); next.push(img); }
        else if (front && queue.includes(img)) { queue.splice(queue.indexOf(img), 1); next.push(img); }
      }
      if (front) queue.unshift(...next); else queue.push(...next);
      pump();
    },
    run(on) { running = on; pump(); },
  };
}

/* Milen stage 1.4: independent groups and exact IX2 quadratic timelines.
   The timelines run as Web Animations (opacity and transform, on the compositor) instead of per-frame inline style
   writes: those cost a style, layerize and commit pass on every frame (the 390 menu ran at ~30fps under 4x CPU), and
   every write was a DOM mutation that session-recording scripts (Clarity) observe and serialise. Inline styles hold
   the committed end state; a running animation overrides it. */
class XinzuoMobileNavigation extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.dialog = this.querySelector('dialog');
    this.scroller = this.querySelector('.xz-menu-wrp-mobile');
    this.closeButton = this.querySelector('[data-v2-mobile-close]');
    this.groups = new Map();
    this.viewportBreakpoint = this.breakpoint();
    this.motion = { x: 24, opacity: 0 };
    this.restored = true;
    this.paint();
    this.addEventListener('click', (event) => {
      const close = event.target.closest('[data-v2-mobile-close]');
      const toggle = event.target.closest('[data-v2-mobile-toggle]');
      if (close) this.closeDialog();
      else if (toggle && this.contains(toggle)) {
        const group = toggle.closest('[data-v2-mobile-group]');
        this.setGroup(group, toggle.getAttribute('aria-expanded') !== 'true');
      }
      const search = event.target.closest('[data-v2-mobile-search]');
      if (search && !event.button && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const modal = document.getElementById('search-modal');
        if (typeof modal?.showDialog === 'function') {
          event.preventDefault();
          const opener = this.opener?.isConnected ? this.opener : document.querySelector('[data-v2-menu]');
          this.closeDialog(true, false);
          // Search must capture the intended return target before taking input
          // focus; closing navigation clears its retained opener.
          if (opener?.isConnected && window.innerWidth <= 991) opener.focus({ preventScroll: true });
          modal.showDialog();
        }
      }
    }, { signal });
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeDialog(); }, { signal });
    // The menu opens as a non-modal dialog (see showDialog), so it owns Escape and keeps Tab inside itself.
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); this.closeDialog(); return; }
      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      const items = tabbables(this.dialog);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }, { signal });
    document.addEventListener('focusin', (event) => {
      // The Klaviyo signup popup sits above this overlay (xz-v2-home-body.css); let focus stay in it.
      if (event.target.closest?.('[data-kl-scroll-locking-modal]')) return;
      if (this.dialog.open && !this.closing && !this.dialog.contains(event.target)) (tabbables(this.dialog)[0] || this.closeButton).focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('close', () => {
      if (!this.dialog.open) this.restore();
    }, { signal });
    window.addEventListener('resize', () => {
      const breakpoint = this.breakpoint();
      const changed = breakpoint !== this.viewportBreakpoint;
      this.viewportBreakpoint = breakpoint;
      if (window.Shopify?.designMode) return;
      // Source IX2 resets initial states whenever its media-query key changes.
      if (changed) {
        this.closeDialog(true);
        for (const group of this.querySelectorAll('[data-v2-mobile-group]')) this.setGroup(group, false, true);
      } else if (window.innerWidth > 991) this.closeDialog(true);
    }, { signal });
    const reveal = (event) => {
      let target = event.target;
      if (!this.contains(target)) {
        target = [...this.querySelectorAll('[data-section-id]')]
          .find((node) => node.dataset.sectionId === event.detail?.sectionId);
      }
      if (!target || !this.contains(target)) return;
      // Desktop editor: the same group feeds the header mega menu, so reveal that instead.
      const desktopGroup = target.closest('[data-v2-mobile-group]') || target.querySelector('[data-v2-mobile-group]');
      if (window.innerWidth > 991 && desktopGroup && window.XZV2Header?.revealDesktop?.(desktopGroup.dataset.v2MobileKey, event.detail?.blockId)) return;
      this.showDialog(undefined, true);
      const group = target.closest('[data-v2-mobile-group]') || target.querySelector('[data-v2-mobile-group]');
      if (group) this.setGroup(group, true, true);
      const block = event.detail?.blockId && [...target.querySelectorAll('[data-v2-mobile-block-id]')]
        .find((node) => node.dataset.v2MobileBlockId === event.detail.blockId);
      (block || target).scrollIntoView({ block: 'nearest', behavior: 'auto' });
    };
    document.addEventListener('shopify:section:select', reveal, { signal });
    document.addEventListener('shopify:block:select', reveal, { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      for (const [group, state] of this.groups) if (event.target.contains(group)) {
        state.animation?.cancel();
        this.groups.delete(group);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.events?.abort();
    this.warmer?.run(false);
    this.stopMotion(false);
    for (const state of this.groups?.values() || []) state.animation?.cancel();
    this.groups?.clear();
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
    setOverlay('menu', false);
  }

  breakpoint() { return window.innerWidth > 991 ? 'main' : window.innerWidth > 767 ? 'medium' : window.innerWidth > 479 ? 'small' : 'tiny'; }
  round(value) { return window.XZV2FrameClock?.optimizeFloat(value) ?? Math.round(value * 100000) / 100000; }
  reduced() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }

  paint() {
    this.dialog.style.opacity = String(this.motion.opacity);
    const transform = `translate3d(${this.motion.x}px,0,0)`;
    this.scroller.style.transform = transform;
    this.closeButton.style.transform = transform;
  }

  // The state shown now: the running timeline's sample, or the committed state.
  current() {
    const run = this.run;
    if (!run) return { ...this.motion };
    const [fade, move] = run.animations;
    const ease = run.ease;
    return {
      x: this.round(run.from.x + (run.to.x - run.from.x) * ease(progress(move, run.move))),
      opacity: this.round(run.from.opacity + (run.to.opacity - run.from.opacity) * ease(progress(fade, run.fade))),
    };
  }

  // Ends the running timeline where it is (keep = true) or drops it; returns the state it left.
  stopMotion(keep = true) {
    const run = this.run;
    if (!run) return this.motion;
    const state = this.current();
    this.run = null;
    for (const animation of run.animations) { animation.onfinish = null; animation.cancel(); }
    if (keep) { this.motion = state; this.paint(); }
    return this.motion;
  }

  // One timeline from the current state to `to`: opacity over `fade` ms, the slide over `move` ms. The inline style
  // takes the end state at once; the animations run over it and, like IX2, show the start state on their first frame.
  motionTo(to, { move, fade, easing, ease, done }) {
    const from = this.stopMotion();
    this.motion = { ...to };
    this.paint();
    if (this.reduced() || typeof this.dialog.animate !== 'function') { done?.(); return; }
    const slide = [{ transform: `translate3d(${from.x}px,0,0)` }, { transform: `translate3d(${to.x}px,0,0)` }];
    const animations = [
      this.dialog.animate([{ opacity: from.opacity }, { opacity: to.opacity }], { duration: fade, easing }),
      this.scroller.animate(slide, { duration: move, easing }),
      this.closeButton.animate(slide, { duration: move, easing }),
    ];
    const run = this.run = { from, to, move, fade, ease, animations };
    const last = fade > move ? animations[0] : animations[1];
    last.onfinish = () => {
      if (this.run !== run) return;
      this.run = null;
      for (const animation of animations) animation.cancel();
      done?.();
    };
  }

  // Starts (or resumes) the artwork queue: every group's images, the first screen of each group first; `group` (being
  // expanded) moves its own images to the front.
  warm(group) {
    if (!this.warmer) {
      this.warmer = warmQueue(WARM_LIMIT);
      const grids = [...this.querySelectorAll('[data-v2-mobile-grid]')].map((grid) => [...grid.querySelectorAll('img')]);
      this.warmer.add(grids.flatMap((imgs) => imgs.slice(0, FIRST_SCREEN)));
      this.warmer.add(grids.flatMap((imgs) => imgs.slice(FIRST_SCREEN)));
    }
    if (group) this.warmer.add(group.querySelectorAll('[data-v2-mobile-grid] img'), true);
    this.warmer.run(true);
  }

  showDialog(opener, editor = false) {
    if (window.innerWidth > 991 && !editor) return;
    if (this.dialog.open && !this.closing) return;
    // The artwork queue starts (or resumes) once the menu has painted its opening frame: a rAF, then a task.
    requestAnimationFrame(() => setTimeout(() => { if (this.dialog.open && !this.closing) this.warm(); }, 0));
    this.closing = false;
    if (!this.dialog.open) {
      this.restored = false;
      this.opener = opener || document.querySelector('[data-v2-menu]');
      this.stopMotion(false);
      this.motion = { x: 24, opacity: 0 };
      this.paint();
      // Own return focus explicitly, avoiding a second native restoration style
      // pass on close. Programmatic/editor opens must not blur unrelated controls.
      if (this.opener && document.activeElement === this.opener) this.opener.blur();
      // State writes go before show(), so the style pass show() forces (dialog focusing) covers them too.
      this.opener?.setAttribute('aria-expanded', 'true');
      setOverlay('menu', true);
      // Non-modal on purpose. showModal() makes the rest of the page inert, and Chrome applies inertness by
      // restyling every element: about 20ms at 390 and 80ms+ on a 4x-slower phone, all in the tap frame, so the
      // menu appeared 110-340ms after the tap against 55-99ms on Milen's (a plain display toggle). The dialog
      // is fixed, full-viewport and z-index 999999 (his value), carries aria-modal="true", traps Tab and closes
      // on Escape (listeners above); lockScroll stops the page scrolling behind it as before.
      this.dialog.show();
      if (!this.dialog.contains(document.activeElement)) this.closeButton.focus({ preventScroll: true });
      lockScroll(this.dialog);
    }
    this.opener?.setAttribute('aria-expanded', 'true');
    // IX2 shows the menu on its first frame after the tap (display:flex, opacity 0, x 24) and runs the curve from
    // there: fade 200ms, slide 500ms, outQuad. A Web Animation starts on the frame that first shows it, so a slow
    // first frame (styling and painting the 54-card menu) never skips the start of the curve.
    this.motionTo({ x: 0, opacity: 1 }, { move: 500, fade: 200, easing: OUT_QUAD, ease: outQuad });
  }

  closeDialog(immediate = false, returnFocus = true) {
    if (!this.dialog?.open || (this.closing && !immediate)) return;
    this.closing = true;
    this.returnFocus = returnFocus;
    const finish = () => {
      this.dialog.close();
      this.restore();
    };
    if (immediate) {
      this.stopMotion(false);
      this.motion = { x: 88, opacity: 0 };
      this.paint();
      finish();
      return;
    }
    this.motionTo({ x: 88, opacity: 0 }, { move: 100, fade: 100, easing: IN_QUAD, ease: inQuad, done: finish });
  }

  restore() {
    if (this.restored) return;
    this.restored = true;
    this.warmer?.run(false);
    this.stopMotion(false);
    unlockScroll(this.dialog);
    setOverlay('menu', false);
    this.closing = false;
    this.motion = { x: 24, opacity: 0 };
    this.paint();
    const opener = this.opener?.isConnected ? this.opener : document.querySelector('[data-v2-menu]');
    opener?.setAttribute('aria-expanded', 'false');
    if (this.returnFocus !== false && opener?.isConnected && window.innerWidth <= 991) opener.focus({ preventScroll: true });
    this.returnFocus = true;
    this.opener = null;
  }

  setGroup(group, open, immediate = false) {
    if (!group) return;
    const button = group.querySelector('[data-v2-mobile-toggle]');
    const grid = group.querySelector('[data-v2-mobile-grid]');
    const footer = group.querySelector('[data-v2-mobile-footer]');
    const divider = group.querySelector('[data-v2-mobile-divider]');
    const icon = button.querySelector('.ff-icon.ff-is-t-margin');
    const prior = this.groups.get(group);
    if (open) this.warm(group);
    button.setAttribute('aria-expanded', String(open));
    grid.hidden = !open;
    grid.style.display = open ? 'grid' : 'none';
    if (footer) {
      footer.hidden = !open;
      footer.style.display = open ? 'flex' : 'none';
    }
    if (divider) divider.hidden = open;
    // Chevron: 200ms outQuad from wherever it is (a Web Animation over the inline end state).
    const from = prior ? this.groupAngle(group) : (open ? 0 : 180), to = open ? 180 : 0;
    prior?.animation?.cancel();
    const state = { from, to, animation: null };
    this.groups.set(group, state);
    if (icon) {
      icon.style.transform = `rotateZ(${to}deg)`;
      if (!immediate && !this.reduced() && from !== to && typeof icon.animate === 'function') {
        state.animation = icon.animate([{ transform: `rotateZ(${from}deg)` }, { transform: `rotateZ(${to}deg)` }], { duration: 200, easing: OUT_QUAD });
        state.animation.onfinish = () => { if (this.groups.get(group) === state) state.animation = null; };
      }
    }
  }

  groupAngle(group) {
    const state = this.groups.get(group);
    if (!state) return null;
    if (!state.animation) return state.to;
    return this.round(state.from + (state.to - state.from) * outQuad(progress(state.animation, 200)));
  }

  revealGroup(key) {
    this.showDialog(undefined, true);
    const group = [...this.querySelectorAll('[data-v2-mobile-key]')].find((node) => node.dataset.v2MobileKey === key);
    if (group) {
      this.setGroup(group, true, true);
      group.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }
}

if (!customElements.get('xz-v2-mobile-navigation')) customElements.define('xz-v2-mobile-navigation', XinzuoMobileNavigation);
